import { EXTERIOR_MAX_REGULAR } from "@/config/constants";
import type { VehicleTestImages } from "@/types/vehicleTest";
import { is360ExteriorFrame } from "@/types/vehicleTest";
import { isVideoMedia } from "@/lib/isVideoMedia";

export type PhotoSessionSection = keyof VehicleTestImages;

/** Exterior & interior: landscape-only capture. Other sections allow any orientation. */
export function sectionRequiresLandscape(
  section: PhotoSessionSection,
): boolean {
  return section === "exterior" || section === "interior";
}

export function getSectionMax(
  section: PhotoSessionSection,
): number {
  switch (section) {
    case "exterior":
      return EXTERIOR_MAX_REGULAR;
    case "interior":
    case "damage":
    case "serviceImages":
      return 15;
    case "tires":
      return 5;
    default:
      return 15;
  }
}

export function getSectionImageCount(
  images: VehicleTestImages,
  section: PhotoSessionSection,
  tireType?: "primary" | "secondary",
): number {
  switch (section) {
    case "exterior":
      // Photos only — engine video has its own slot and must not block guided capture
      return (images.exterior || []).filter(
        (img) =>
          !is360ExteriorFrame(img) && !isVideoMedia(img.type, img.url),
      ).length;
    case "interior":
      return (images.interior || []).length;
    case "damage":
      return (images.damage || []).length;
    case "serviceImages":
      return (images.serviceImages || []).length;
    case "tires":
      return (images.tires?.[tireType || "primary"] || []).length;
    default:
      return 0;
  }
}

export function getSectionTitleKey(
  section: PhotoSessionSection,
  tireType?: "primary" | "secondary",
): string {
  if (section === "tires") {
    return tireType === "secondary" ? "secondaryTires" : "primaryTires";
  }
  if (section === "serviceImages") return "service";
  return section;
}
